import type { z } from 'zod';

export type SseEventTarget<TPayload> = {
  type: string | ((payload: TPayload) => string);
  id: (payload: TPayload) => string;
};

export type SseEventDef<TPayload, TType extends string = string> = {
  type: TType;
  payload: z.ZodType<TPayload>;
  channelPrefix: string;
  channelKey: (payload: TPayload) => string;
  target?: SseEventTarget<TPayload>;
};

export type SseSubscribe = <TPayload>(
  eventDef: SseEventDef<TPayload>,
  callback: (event: TPayload) => void,
  options?: { filter?: (event: TPayload) => boolean },
) => () => void;

export type SseSubscribeReconnect = (callback: () => void) => () => void;

export function defineSseEvent<
  TSchema extends z.ZodType,
  TType extends string,
>(args: {
  type: TType;
  payload: TSchema;
  channelPrefix: string;
  channelKey: (payload: z.output<TSchema>) => string;
  target?: SseEventTarget<z.output<TSchema>>;
}): SseEventDef<z.output<TSchema>, TType> {
  return {
    type: args.type,
    payload: args.payload as z.ZodType<z.output<TSchema>>,
    channelPrefix: args.channelPrefix,
    channelKey: args.channelKey,
    target: args.target,
  };
}

export function buildSseChannelId<T>(def: SseEventDef<T>, payload: T): string {
  return `${def.channelPrefix}:${def.channelKey(payload)}`;
}
